import { useState, useEffect, FC } from 'react';

import { Flex, Select, Spin, Typography, Button, Input } from 'antd';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useTheme } from 'styled-components';
import useSWR from 'swr';

import IconError from '@/assets/icons/icon-error.svg?react';
import IconInfo from '@/assets/icons/icon-info.svg?react';
import Tooltip from '@/components/RTooltip';
import { Scenario } from '@/types/scenario';
import { SimulationResult } from '@/types/simulations';

import CashFlowStorageTable from './CashFlowStorage';
import CashFlowWithoutStorageTable from './CashFlowWithoutStorage';
import CashFlowWithStorageTable from './CashFlowWithStorage';
import StyledCard from './styles';

interface HurdleRateState {
  temp: string;
  custom: number;
  default: number;
}
type CashFlowTableProps = {
  simulationId?: number;
};
const CashFlowTable: FC<CashFlowTableProps> = ({ simulationId }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const params = useParams<Record<string, string>>();
  const resultId = simulationId || params.resultId;
  const [storageSelection, setStorageSelection] = useState('withStorage');
  const [hurdleRate, setHurdleRate] = useState<HurdleRateState>({
    temp: '',
    custom: 0,
    default: 0,
  });

  const {
    data: simulationData,
    isLoading,
    error,
  } = useSWR<SimulationResult>(`/simulation/${resultId}/`, {
    revalidateOnFocus: false,
  });
  const scenarioId = simulationData?.scenario?.id;
  const { data: scenarioData } = useSWR<Scenario>(`/scenario/${scenarioId}/`, {
    revalidateOnFocus: false,
  });

  useEffect(() => {
    if (scenarioData?.hurdleRate !== undefined) {
      setHurdleRate({
        temp: scenarioData.hurdleRate.toString(),
        custom: scenarioData.hurdleRate,
        default: scenarioData.hurdleRate,
      });
    }
  }, [scenarioData]);

  const handleHurdleRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setHurdleRate((prev) => ({ ...prev, temp: '' }));
      return;
    }
    const numValue = Number(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      setHurdleRate((prev) => ({ ...prev, temp: value }));
    }
  };

  const applyHurdleRate = () => {
    const numValue = Number(hurdleRate.temp);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
      setHurdleRate((prev) => ({ ...prev, custom: numValue }));
    }
  };

  const handleStorageSelection = (value: string) => {
    setStorageSelection(value);
    setHurdleRate((prev) => ({
      ...prev,
      temp: prev.default.toString(),
      custom: prev.default,
    }));
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Flex justify="center" align="center" style={{ height: 361 }}>
          <Spin style={{ margin: '20px' }} />
        </Flex>
      );
    }

    if (error) {
      return (
        <Flex
          vertical
          gap={12}
          justify="center"
          align="center"
          style={{ height: 361 }}
        >
          <IconError width={50} height={50} fill={theme.colors.tagFailure} />
          <Typography.Text className="fs-14-regular text-gray-color">
            {t('result.error.fetchDataError')}
          </Typography.Text>
        </Flex>
      );
    }

    switch (storageSelection) {
      case 'withStorage':
        return (
          <CashFlowWithStorageTable
            hurdleRate={hurdleRate.custom}
            defaultHurdleRate={hurdleRate.default}
            simulationId={simulationId}
          />
        );
      case 'withoutStorage':
        return (
          <CashFlowWithoutStorageTable
            hurdleRate={hurdleRate.custom}
            defaultHurdleRate={hurdleRate.default}
          />
        );
      case 'storage':
        return (
          <CashFlowStorageTable
            hurdleRate={hurdleRate.custom}
            defaultHurdleRate={hurdleRate.default}
          />
        );
      default:
        return null;
    }
  };

  return (
    <StyledCard
      title={
        <Flex gap={8} align="center">
          <Typography.Title level={4} className="fs-17-bold">
            {t('result.finance.cashFlowTable.title')}
          </Typography.Title>
          <Tooltip
            title={t('result.finance.cashFlowTable.title')}
            description={t('result.finance.cashFlowTable.tooltip')}
          >
            <IconInfo />
          </Tooltip>
        </Flex>
      }
      styles={{ header: { padding: '17.25px 24px' } }}
      $paddingBody="12px 0"
      style={{ marginTop: '24px' }}
      extra={
        <Flex gap={16} align="center">
          <Flex gap={8} align="center">
            <Typography.Text>
              {t('result.finance.cashFlowTable.hurdleRate')}
            </Typography.Text>
            <motion.div
              whileTap={{ scale: 1.02 }}
              transition={{ type: 'spring', stiffness: 300, damping: 24 }}
            >
              <Input
                type="number"
                onChange={handleHurdleRateChange}
                value={hurdleRate.temp}
                style={{ width: '120px' }}
                min={0}
                max={100}
                addonAfter={
                  <Button
                    type="text"
                    size="small"
                    onClick={applyHurdleRate}
                    className="hurdle-rate-button"
                    icon={'↵'}
                  />
                }
                onPressEnter={applyHurdleRate}
              />
            </motion.div>
          </Flex>
          <motion.div
            whileTap={{ scale: 1.1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 24 }}
          >
            <Select
              placeholder={t(
                'result.finance.cashFlowTable.storageSelection.placeholder'
              )}
              style={{ minWidth: '150px', width: '150px' }}
              value={storageSelection}
              onChange={handleStorageSelection}
            >
              <Select.Option value="withStorage">
                {t('result.finance.cashFlowTable.storageSelection.withStorage')}
              </Select.Option>
              <Select.Option value="withoutStorage">
                {t(
                  'result.finance.cashFlowTable.storageSelection.withoutStorage'
                )}
              </Select.Option>
              <Select.Option value="storage">
                {t('result.finance.cashFlowTable.storageSelection.storage')}
              </Select.Option>
            </Select>
          </motion.div>
        </Flex>
      }
    >
      {renderContent()}
    </StyledCard>
  );
};

export default CashFlowTable;
